import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

/**
 * Stati possibili del download
 */
export enum DownloadStatus {
  DOWNLOADING = "DOWNLOADING",
  COMPLETED = "COMPLETED",
  ABORTED = "ABORTED"
}

/**
 * Service per gestire lo stato dei download nel componente ape-download-button
 *
 * CASI D'USO:
 * - Centralizzare lo stato dei download
 * - Evitare la propagazione manuale degli eventi di completamento
 * - Gestire multipli download contemporanei
 */
@Injectable({ providedIn: "root" })
export class ApeDownloadButtonService {
  private readonly downloadState = new BehaviorSubject<DownloadStatus>(DownloadStatus.COMPLETED);

  private currentDownloadId: string | null = null;

  public readonly downloadState$ = this.downloadState.asObservable();

  /**
   * Inizia un download e restituisce l'ID associato
   * @returns ID univoco del download
   */
  public startDownload(): string {
    const id = this.generateId();
    this.currentDownloadId = id;
    this.downloadState.next(DownloadStatus.DOWNLOADING);
    return id;
  }

  /**
   * Completa un download
   * @param downloadId - ID del download da completare (opzionale per retrocompatibilità)
   */
  public completeDownload(downloadId?: string): void {
    // Se viene fornito un ID e non corrisponde a quello corrente, ignoriamo il completamento
    // Questo evita che un download vecchio (D1) completi erroneamente un download nuovo in corso (D2)
    if (downloadId && this.currentDownloadId !== downloadId) {
      return;
    }

    this.currentDownloadId = null;
    this.downloadState.next(DownloadStatus.COMPLETED);
  }

  /**
   * Verifica se c'è un download in corso
   * @returns True se c'è un download in corso
   */
  public isDownloading(): boolean {
    return this.downloadState.value === DownloadStatus.DOWNLOADING;
  }

  /**
   * Cancella un download in corso
   */
  public cancelDownload(): void {
    this.currentDownloadId = null;
    this.downloadState.next(DownloadStatus.ABORTED);
  }

  /**
   * Verifica se un download specifico è ancora valido (cioè è quello corrente e in corso)
   * @param downloadId - ID del download da verificare
   * @returns True se il download è valido
   */
  public isDownloadValid(downloadId: string): boolean {
    return this.currentDownloadId === downloadId && this.downloadState.value === DownloadStatus.DOWNLOADING;
  }

  /**
   * Resetta lo stato del download
   */
  public reset(): void {
    this.currentDownloadId = null;
    this.downloadState.next(DownloadStatus.COMPLETED);
  }

  /**
   * Genera un ID univoco per il download
   */
  private generateId(): string {
    return new Date()
      .toISOString();
  }
}
